// src/products/analysis/services/finnhubService.js
// Updated to use backend proxy endpoints (API keys stored securely in Supabase)
import { getAuthHeaders } from '../../../services/authService';

const API_BASE_URL = '/api/analysis';

/**
 * Helper to check if response is HTML (backend not available)
 */
const checkResponseType = (response) => {
  const contentType = response.headers.get('content-type') || '';
  if (contentType.includes('text/html')) {
    throw new Error('Backend API not available. Please ensure the backend server is running or deploy to Vercel.');
  }
};

/**
 * Get company profile data
 * Returns null if data not available (common for some international stocks)
 */
export const getCompanyProfile = async (symbol) => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(
      `${API_BASE_URL}/finnhub/profile?symbol=${encodeURIComponent(symbol)}`,
      { headers }
    );
    
    checkResponseType(response);
    
    if (!response.ok) {
      if (response.status === 404) {
        return null;
      }
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    // Check if response is empty object (common for international stocks)
    if (!data || Object.keys(data).length === 0) {
      return null;
    }
    return data;
  } catch (error) {
    console.warn('Finnhub profile error (non-fatal):', error.message);
    return null;
  }
};

/**
 * Get company quote (current price)
 * Returns null if data not available (common for some international stocks)
 */
export const getCompanyQuote = async (symbol) => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(
      `${API_BASE_URL}/finnhub/quote?symbol=${encodeURIComponent(symbol)}`,
      { headers }
    );
    
    checkResponseType(response);
    
    if (!response.ok) {
      if (response.status === 404) {
        return null;
      }
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    
    if (!data || (typeof data === 'object' && Object.keys(data).length === 0)) {
      return null;
    }
    
    if (data.c === 0 && data.p === 0 && data.h === 0 && data.l === 0 && 
        (data.c === undefined || data.p === undefined)) {
      return null;
    }
    
    return data;
  } catch (error) {
    console.warn('Finnhub quote error (non-fatal):', error.message);
    return null;
  }
};

/**
 * Get company basic financials
 * Returns null if data not available (very common for international stocks)
 */
export const getCompanyBasicFinancials = async (symbol) => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(
      `${API_BASE_URL}/finnhub/metric?symbol=${encodeURIComponent(symbol)}`,
      { headers }
    );
    
    checkResponseType(response);
    
    if (!response.ok) {
      if (response.status === 404 || response.status === 429) {
        return null;
      }
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    if (!data || !data.metric || Object.keys(data.metric).length === 0) {
      return null;
    }
    return data;
  } catch (error) {
    console.warn('Finnhub financials error (non-fatal):', error.message);
    return null;
  }
};

/**
 * Search for companies by symbol or name
 * Supports global stocks with exchange suffixes:
 * - US: AAPL, MSFT (no suffix)
 * - Australia: BHP.AX, CBA.AX
 * - UK: BP.L, SHEL.L
 * - Canada: SHOP.TO
 * - Germany: BMW.DE, SAP.DE
 * - France: TTE.PA, LVMH.PA
 * - Japan: 7203.T (or company name search)
 * - And many more global exchanges
 */
export const searchCompanies = async (query) => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(
      `${API_BASE_URL}/finnhub/search?q=${encodeURIComponent(query)}`,
      { headers }
    );
    
    checkResponseType(response);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    return data.result || [];
  } catch (error) {
    console.error('Finnhub search error:', error);
    throw error;
  }
};

/**
 * Get company news articles
 * @param {string} symbol - Stock symbol
 * @param {number} days - Number of days to look back (default: 30)
 */
export const getCompanyNews = async (symbol, days = 30) => {
  try {
    const toDate = new Date();
    const fromDate = new Date();
    fromDate.setDate(fromDate.getDate() - days);
    
    const to = toDate.toISOString().split('T')[0];
    const from = fromDate.toISOString().split('T')[0];
    
    const headers = await getAuthHeaders();
    const response = await fetch(
      `${API_BASE_URL}/finnhub/news?symbol=${encodeURIComponent(symbol.toUpperCase())}&from=${from}&to=${to}`,
      { headers }
    );
    
    checkResponseType(response);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    
    if (!Array.isArray(data)) {
      throw new Error('Invalid response format from API');
    }
    
    return data.slice(0, 50).map(article => ({
      headline: article.headline || 'No headline',
      summary: article.summary || 'No summary available',
      source: article.source || 'Unknown source',
      datetime: article.datetime || Date.now() / 1000,
      url: article.url || '#',
      category: article.category || 'General',
      image: article.image || null,
      related: article.related || symbol
    }));
  } catch (error) {
    console.error('Finnhub news error:', error);
    throw error;
  }
};

/**
 * Get comprehensive stock data including shares outstanding, market cap, EV, and price
 */
export const getStockData = async (symbol) => {
  try {
    // Normalize symbol - ensure uppercase and handle exchange suffixes
    const cleanSymbol = symbol.toUpperCase().trim();
    
    // Fetch all data in parallel - functions now return null instead of throwing for international stocks
    const [profileData, quoteData, financialsData] = await Promise.all([
      getCompanyProfile(cleanSymbol),
      getCompanyQuote(cleanSymbol),
      getCompanyBasicFinancials(cleanSymbol)
    ]);

    // At least quote data is required (most basic data point)
    if (!quoteData && !profileData) {
      throw new Error(`Unable to fetch data for ${cleanSymbol}. This symbol may not be available or the exchange may not be supported on the free tier. Try using the symbol with exchange suffix (e.g., BHP.AX for Australian stocks).`);
    }
    
    // Check if we have any valid price data
    const hasValidPrice = quoteData && (
      quoteData.c || quoteData.c === 0 || 
      quoteData.p || quoteData.p === 0 ||
      quoteData.currentPrice || quoteData.currentPrice === 0
    );
    
    if (!hasValidPrice && !profileData) {
      throw new Error(`Unable to fetch price data for ${cleanSymbol}. Quote data may be unavailable for this exchange.`);
    }
    
    // Extract relevant data
    // Shares outstanding from Finnhub profile API (in actual shares, not millions)
    // Note: profile.shareOutstanding is typically in actual shares
    const sharesOutstandingRaw = profileData?.shareOutstanding || financialsData?.metric?.sharesOutstanding || null;
    
    // Check if it's in millions (common format: if it's a small number like 15.7 for Apple, it's billions)
    // Apple has ~15.7B shares, so if the number is < 100, it might be in billions, multiply by 1B
    // If it's > 1000, assume it's already in actual shares
    let sharesOutstanding = sharesOutstandingRaw;
    if (sharesOutstandingRaw) {
      if (sharesOutstandingRaw < 100) {
        // Likely in billions (e.g., 15.7 = 15.7B shares)
        sharesOutstanding = sharesOutstandingRaw * 1000000000;
      } else if (sharesOutstandingRaw < 1000) {
        // Likely in millions (e.g., 15700 = 15.7B shares)
        sharesOutstanding = sharesOutstandingRaw * 1000000;
      }
      // Otherwise assume it's already in actual shares
    }
    
    // Try multiple fields for current price (different APIs use different fields)
    const currentPrice = quoteData?.c || 
                        quoteData?.currentPrice || 
                        quoteData?.price || 
                        quoteData?.p ||  // previous close as fallback
                        (quoteData?.c === 0 ? 0 : null); // Allow 0 as valid price
    
    // Market Cap calculation: shares outstanding * current price
    // Or use direct market cap from API if available (which is usually more accurate)
    const marketCapFromAPI = financialsData?.metric?.marketCapitalization;
    const marketCapCalculated = sharesOutstanding && currentPrice ? sharesOutstanding * currentPrice : null;
    // Prefer API value if available (it's usually more accurate), otherwise use calculated
    const marketCap = marketCapFromAPI || marketCapCalculated;
    
    // Enterprise Value formula: EV = Market Cap + Total Debt - Cash & Equivalents
    // Finnhub provides this directly from their financials/metrics endpoint
    // If not available, we could calculate: marketCap + debt - cash
    const ev = financialsData?.metric?.enterpriseValue || null;
    
    // Log for debugging
    console.log('Stock Data Debug:', {
      symbol: cleanSymbol,
      sharesRaw: sharesOutstandingRaw,
      sharesCalculated: sharesOutstanding,
      currentPrice,
      marketCapFromAPI,
      marketCapCalculated,
      marketCap,
      ev,
      hasProfile: !!profileData,
      hasQuote: !!quoteData,
      hasFinancials: !!financialsData,
      profileShares: profileData?.shareOutstanding,
      financialsShares: financialsData?.metric?.sharesOutstanding
    });

    return {
      symbol: cleanSymbol,
      name: profileData?.name || quoteData?.name || 'N/A',
      exchange: profileData?.exchange || cleanSymbol.includes('.') ? cleanSymbol.split('.')[1] : 'N/A',
      currency: profileData?.currency || quoteData?.currency || 'USD',
      sharesOutstanding: sharesOutstanding ? formatNumber(sharesOutstanding) : 'N/A',
      marketCap: marketCap ? formatCurrency(marketCap, profileData?.currency || quoteData?.currency || 'USD') : 'N/A',
      enterpriseValue: ev ? formatCurrency(ev, profileData?.currency || quoteData?.currency || 'USD') : 'N/A',
      currentPrice: currentPrice ? formatCurrency(currentPrice, profileData?.currency || quoteData?.currency || 'USD') : 'N/A',
      priceChange: quoteData?.d !== undefined ? formatNumber(quoteData.d) : null,
      priceChangePercent: quoteData?.dp !== undefined ? formatNumber(quoteData.dp) + '%' : null,
      high: quoteData?.h || null,
      low: quoteData?.l || null,
      open: quoteData?.o || null,
      previousClose: quoteData?.pc || quoteData?.p || null,
      logo: profileData?.logo || null,
      weburl: profileData?.weburl || null,
      // Raw values for calculations
      _sharesOutstanding: sharesOutstanding,
      _currentPrice: currentPrice,
      _marketCap: marketCap,
      _enterpriseValue: ev
    };
  } catch (error) {
    console.error('Error fetching stock data:', error);
    throw error;
  }
};

/**
 * Format large numbers (e.g., shares outstanding)
 */
function formatNumber(num) {
  if (!num) return 'N/A';
  if (num >= 1e9) return (num / 1e9).toFixed(2) + 'B';
  if (num >= 1e6) return (num / 1e6).toFixed(2) + 'M';
  if (num >= 1e3) return (num / 1e3).toFixed(2) + 'K';
  return num.toLocaleString();
}

/**
 * Format currency values
 */
function formatCurrency(value, currency = 'USD') {
  if (!value) return 'N/A';
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
  return formatter.format(value);
}

/**
 * Get stock candlestick data using Alpha Vantage as primary (more reliable for free tier)
 * Falls back to Finnhub if Alpha Vantage fails
 * @param {string} symbol - Stock symbol
 * @param {string} resolution - 'D' for daily, 'W' for weekly, 'M' for monthly
 * @param {number} fromTimestamp - Unix timestamp (seconds)
 * @param {number} toTimestamp - Unix timestamp (seconds)
 */
export const getStockCandles = async (symbol, resolution = 'D', fromTimestamp, toTimestamp) => {
  const cleanSymbol = symbol.toUpperCase().trim();
  
  // Try Alpha Vantage first (free tier allows 5 calls/min, 500/day)
  try {
    return await getStockCandlesAlphaVantage(cleanSymbol, resolution);
  } catch (alphaError) {
    console.log('Alpha Vantage failed, trying Finnhub:', alphaError.message);
    
    // Fallback to Finnhub
    try {
      return await getStockCandlesFinnhub(cleanSymbol, resolution, fromTimestamp, toTimestamp);
    } catch (finnhubError) {
      // If both fail, throw a helpful error
      if (finnhubError.message.includes('403')) {
        throw new Error('Chart data requires a premium API plan. Please use a paid Finnhub or Alpha Vantage subscription, or try a different time period.');
      }
      throw new Error(`Failed to fetch chart data: ${finnhubError.message || alphaError.message}`);
    }
  }
};

/**
 * Get stock candles from Alpha Vantage
 */
const getStockCandlesAlphaVantage = async (symbol, resolution = 'D') => {
  const outputsize = resolution === 'W' || resolution === 'M' ? 'full' : 'compact';
  
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(
      `${API_BASE_URL}/alphavantage/time-series?symbol=${encodeURIComponent(symbol)}&function=TIME_SERIES_DAILY&outputsize=${outputsize}`,
      { headers }
    );
    
    checkResponseType(response);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    
    if (data['Error Message']) {
      throw new Error(data['Error Message']);
    }
    
    if (data['Note']) {
      throw new Error('API rate limit exceeded. Please try again later.');
    }
    
    const timeSeries = data['Time Series (Daily)'];
    if (!timeSeries) {
      throw new Error('No time series data available');
    }
    
    const candles = Object.entries(timeSeries)
      .map(([date, values]) => ({
        date: new Date(date).toISOString(),
        timestamp: new Date(date).getTime(),
        open: parseFloat(values['1. open']),
        high: parseFloat(values['2. high']),
        low: parseFloat(values['3. low']),
        close: parseFloat(values['4. close']),
        volume: parseInt(values['5. volume'])
      }))
      .sort((a, b) => a.timestamp - b.timestamp);
    
    if (resolution === 'W') {
      const weeklyCandles = [];
      for (let i = 0; i < candles.length; i += 7) {
        weeklyCandles.push(candles[i]);
      }
      return weeklyCandles;
    }
    
    return candles.slice(-100);
  } catch (error) {
    console.error('Alpha Vantage candles error:', error);
    throw error;
  }
};

/**
 * Get stock candles from Finnhub (fallback)
 */
const getStockCandlesFinnhub = async (symbol, resolution = 'D', fromTimestamp, toTimestamp) => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(
      `${API_BASE_URL}/finnhub/candles?symbol=${encodeURIComponent(symbol)}&resolution=${resolution}&from=${fromTimestamp}&to=${toTimestamp}`,
      { headers }
    );
    
    checkResponseType(response);
    
    if (!response.ok) {
      if (response.status === 403) {
        throw new Error('403: API endpoint requires premium subscription');
      }
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    
    if (data.s !== 'ok') {
      if (data.s === 'no_data') {
        throw new Error(`No data available for ${symbol}`);
      }
      throw new Error(data.s || 'Failed to fetch candle data');
    }

    if (!data.t || !Array.isArray(data.t) || data.t.length === 0) {
      throw new Error(`No data points returned for ${symbol}`);
    }

    const candles = data.t.map((timestamp, index) => ({
      date: new Date(timestamp * 1000).toISOString(),
      timestamp: timestamp * 1000,
      open: data.o && data.o[index] ? parseFloat(data.o[index]) : null,
      high: data.h && data.h[index] ? parseFloat(data.h[index]) : null,
      low: data.l && data.l[index] ? parseFloat(data.l[index]) : null,
      close: data.c && data.c[index] ? parseFloat(data.c[index]) : null,
      volume: data.v && data.v[index] ? parseInt(data.v[index]) : null
    })).filter(candle => candle.close !== null && candle.close !== undefined);

    return candles;
  } catch (error) {
    console.error('Finnhub candles error:', error);
    throw error;
  }
};

/**
 * Get timestamps for different time periods
 */
export const getTimePeriodTimestamps = (period) => {
  const now = Math.floor(Date.now() / 1000); // Current time in seconds
  const periods = {
    '7d': now - (7 * 24 * 60 * 60),
    '30d': now - (30 * 24 * 60 * 60),
    '60d': now - (60 * 24 * 60 * 60),
    '90d': now - (90 * 24 * 60 * 60),
    '180d': now - (180 * 24 * 60 * 60),
    '1y': now - (365 * 24 * 60 * 60),
    '5y': now - (5 * 365 * 24 * 60 * 60)
  };
  
  return {
    from: periods[period] || periods['90d'],
    to: now
  };
};

/**
 * Get resolution string for Alpha Vantage (maps to their API)
 */
export const getAlphaVantageResolution = (period) => {
  // Alpha Vantage only supports daily for free tier, we'll filter client-side
  return 'D'; // Always daily, we'll aggregate if needed
};

